import { useEffect, useState } from 'react';
import { call } from './api';
import { prefsNow } from './prefs';
import { fromPlexTrack, type MusicTrack } from './musicPlayer';
import type { PlexClient, PlexItem } from './plex';

/**
 * Een nummer uit een lijst (Deezer, Apple Music, Last.fm) afspeelbaar maken: eerst in je Plex-muziek
 * (titel + artiest), daarna in je muziek-addons — of andersom, volgens je volgorde van bronnen.
 */

interface Container<T> { MediaContainer: T }

let plex: PlexClient | null = null;
let musicSections: Promise<string[]> | null = null;
export function setResolverPlex(p: PlexClient) {
  plex = p;
  musicSections = null;
}

const norm = (x: string) => x.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/\s*[([].*?(feat|ft|with|remaster|version|edit|mix|live).*?[)\]]/g, '').replace(/\b(feat|ft)\.?\s.*$/, '')
  .replace(/[^a-z0-9]+/g, ' ').trim();

async function inPlex(t: MusicTrack): Promise<MusicTrack | null> {
  if (!plex) return null;
  const client = plex;
  musicSections ??= client.libraries().then((ls) => ls.filter((l) => l.type === 'artist').map((l) => l.key)).catch(() => []);
  const secs = await musicSections;
  if (!secs.length) return null;
  const wt = norm(t.title);
  const wa = norm(t.artist).split(' ')[0] ?? '';
  for (const k of secs) {
    const d = await client.get<Container<{ Hub?: { type: string; Metadata?: PlexItem[] }[] }>>('/hubs/search', { query: t.title, sectionId: k, limit: 15 }).catch(() => null);
    const tracks = (d?.MediaContainer.Hub ?? []).filter((h) => h.type === 'track').flatMap((h) => h.Metadata ?? []);
    const hit = tracks.find((m) => {
      const nt = norm(m.title);
      const na = norm(`${m.originalTitle ?? ''} ${m.grandparentTitle ?? ''}`);
      return (nt === wt || nt.startsWith(wt) || wt.startsWith(nt)) && (!wa || na.includes(wa));
    });
    if (hit) return fromPlexTrack(client, hit);
  }
  return null;
}

async function inAddons(t: MusicTrack): Promise<MusicTrack | null> {
  const r = await call<{ track: MusicTrack | null }>('/api/music/resolve', {
    method: 'POST',
    body: JSON.stringify({ title: t.title, artist: t.artist, isrc: t.isrc ?? null, duration: t.duration ?? null }),
  }).catch(() => ({ track: null }));
  return r.track ? { ...r.track, artwork: r.track.artwork ?? t.artwork } : null;
}

const done = new Map<string, MusicTrack | null>();
const running = new Map<string, Promise<MusicTrack | null>>();
const listeners = new Set<() => void>();
const keyOf = (t: MusicTrack) => `${norm(t.artist)}|${norm(t.title)}`;

/** Zoekt (één keer) waar dit nummer kan spelen. */
export function resolveTrack(t: MusicTrack): Promise<MusicTrack | null> {
  if (t.source !== 'catalog') return Promise.resolve(t);
  const key = keyOf(t);
  if (done.has(key)) return Promise.resolve(done.get(key)!);
  const busy = running.get(key);
  if (busy) return busy;
  const order = prefsNow().sourceOrder;
  const p = (async () => {
    const steps = order === 'addons-first' ? [inAddons, inPlex] : order === 'addons-only' ? [inAddons] : order === 'plex-only' ? [inPlex] : [inPlex, inAddons];
    for (const step of steps) {
      const r = await step(t).catch(() => null);
      if (r) return r;
    }
    return null;
  })().then((r) => {
    done.set(key, r);
    running.delete(key);
    listeners.forEach((l) => l());
    return r;
  });
  running.set(key, p);
  return p;
}

export function resolvedNow(t: MusicTrack): MusicTrack | null | undefined {
  return t.source === 'catalog' ? done.get(keyOf(t)) : t;
}

/**
 * Voor een lijst: "speelt via" opzoeken, een paar tegelijk (zodat Plex en de addons niet overspoeld worden).
 * Geeft een teller terug die verandert als er iets bekend wordt.
 */
export function useResolveAll(tracks: MusicTrack[] | null, limit = 100): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const l = () => setTick((n) => n + 1);
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  }, []);
  useEffect(() => {
    if (!tracks) return;
    let stop = false;
    const todo = tracks.filter((t) => t.source === 'catalog').slice(0, limit);
    let i = 0;
    const worker = async () => {
      while (!stop && i < todo.length) {
        const t = todo[i++];
        await resolveTrack(t);
      }
    };
    void Promise.all([worker(), worker(), worker()]);
    return () => {
      stop = true;
    };
  }, [tracks, limit]);
  return tick;
}

/** Label voor een (opgezocht) nummer. */
export function viaLabel(t: MusicTrack): { text: string; tone: 'plex' | 'addon' | 'none' | 'busy' } {
  const r = resolvedNow(t);
  if (r === undefined) return { text: 'Zoeken…', tone: 'busy' };
  if (r === null) return { text: 'Niet gevonden', tone: 'none' };
  if (r.source === 'plex') return { text: 'Plex', tone: 'plex' };
  const more = r.alternatives?.length ?? 0;
  return { text: `${r.addonName ?? 'Addon'}${more ? ` +${more}` : ''}`, tone: 'addon' };
}
